import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Check } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { billingIntervalLabel } from '@/lib/plans';

export interface PublicPlan {
  key: string;
  name: string;
  description?: string;
  price: number | null;
  currency: string;
  interval: string;
  creditLimit: number;
  topUpRate: number | null;
  topUpMax?: number | null;
  // Per-message credit rates; null => unpriced (Enterprise), shown as "Custom".
  whatsappInitCredits?: number | null;
  whatsappNextCredits?: number | null;
  smsCredits?: number | null;
  isPopular?: boolean;
  marketingFeatures?: string[];
  isCustom?: boolean;
}

export function formatPlanPrice(p: PublicPlan) {
  if (p.price == null) return 'Custom';
  return `${p.currency?.toUpperCase()} ${p.price.toLocaleString()}`;
}

// Reusable, data-driven plan grid. Parent decides what "subscribe" does
// (handle auth, start checkout, etc.) via onSubscribe.
export default function PlanSelector({
  highlightKey,
  busyKey = null,
  ctaLabel = 'Choose plan',
  columns = 3,
  extraPlans,
  onSubscribe,
}: {
  highlightKey?: string | null;
  busyKey?: string | null;
  ctaLabel?: string;
  columns?: 3 | 4;
  // Plans not in the public catalog (e.g. a workspace's bound custom/enterprise plan).
  // Prepended to the fetched plans and deduped by key.
  extraPlans?: PublicPlan[];
  onSubscribe: (plan: PublicPlan) => void;
}) {
  const gridCols = columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3';
  const [publicPlans, setPublicPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    apiFetch<{ plans: PublicPlan[] }>('/plans')
      .then((r) => { if (active) setPublicPlans(r.plans || []); })
      .catch(() => { /* noop */ })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  // When a workspace has its own custom/enterprise plan, it takes the place of the public
  // "Contact us" enterprise card (price == null) so the total stays at four cards.
  const hasExtra = (extraPlans || []).length > 0;
  const extraKeys = new Set((extraPlans || []).map((p) => p.key));
  const plans = [
    ...(extraPlans || []),
    ...publicPlans.filter((p) => !extraKeys.has(p.key) && !(hasExtra && p.price == null)),
  ];

  return (
    <div className={`grid ${gridCols} gap-6`}>
      {plans.map((plan) => {
        const highlighted = highlightKey === plan.key;
        return (
          <Card key={plan.key} className={`p-6 flex flex-col ${highlighted ? 'ring-2 ring-primary' : ''}`}>
            <h3 className="text-xl font-bold">{plan.name}</h3>
            {plan.description && <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>}
            <div className="mt-4 mb-2">
              <span className="text-3xl font-bold">{formatPlanPrice(plan)}</span>
              {plan.price != null && <span className="text-muted-foreground"> / {billingIntervalLabel(plan.interval)}</span>}
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {plan.creditLimit?.toLocaleString()} credits / {billingIntervalLabel(plan.interval)}
            </p>
            <ul className="space-y-2 text-sm flex-1 mb-6">
              {(plan.marketingFeatures || []).map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <Button className="w-full" disabled={busyKey !== null} onClick={() => onSubscribe(plan)}>
              {busyKey === plan.key ? 'Redirecting…' : plan.price == null ? 'Contact us' : ctaLabel}
            </Button>
          </Card>
        );
      })}
    </div>
  );
}
